import { useState, useEffect, useCallback } from 'react';
import { Employee, Attendance, Leave, Payroll } from '../types/firestore';
import { hrServices } from '../services/hrService';
import { useAuth } from './useAuth';

const getHROwnerId = (
  user?: {
    id: string;
    role?: string;
    createdBy?: string;
    teacherId?: string;
    tenantId?: string;
  } | null
) => user?.createdBy || user?.teacherId || user?.tenantId || user?.id;

export const useEmployees = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const ownerId = getHROwnerId(user);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!ownerId) return;
      const employeesList = await hrServices.employees.getByCreatedBy(ownerId);
      setEmployees(employeesList);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('فشل في تحميل بيانات الموظفين.');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  const fetchActiveEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!ownerId) return;
      const allByCreator = await hrServices.employees.getByCreatedBy(ownerId);
      setEmployees(allByCreator.filter(employee => employee.isActive));
    } catch (err) {
      console.error('Error fetching active employees:', err);
      setError('فشل في تحميل الموظفين النشطين.');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  const createEmployee = useCallback(
    async (employeeData: Omit<Employee, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        setError(null);
        const newEmployeeId = await hrServices.employees.createEmployee(
          employeeData
        );
        await fetchEmployees();
        return newEmployeeId;
      } catch (err) {
        console.error('Error creating employee:', err);
        setError('فشل في إنشاء الموظف.');
        throw err;
      }
    },
    [fetchEmployees]
  );

  const updateEmployee = useCallback(
    async (employeeId: string, updateData: Partial<Employee>) => {
      try {
        setError(null);
        await hrServices.employees.updateEmployee(employeeId, updateData);
        await fetchEmployees();
      } catch (err) {
        console.error('Error updating employee:', err);
        setError('فشل في تحديث بيانات الموظف.');
        throw err;
      }
    },
    [fetchEmployees]
  );

  const deactivateEmployee = useCallback(
    async (employeeId: string, lastWorkingDay?: Date) => {
      try {
        setError(null);
        await hrServices.employees.deactivateEmployee(
          employeeId,
          lastWorkingDay
        );
        await fetchEmployees();
      } catch (err) {
        console.error('Error deactivating employee:', err);
        setError('فشل في تعطيل الموظف.');
        throw err;
      }
    },
    [fetchEmployees]
  );

  useEffect(() => {
    if (user) void fetchEmployees();
  }, [user, fetchEmployees]);

  return {
    employees,
    loading,
    error,
    fetchEmployees,
    fetchActiveEmployees,
    createEmployee,
    updateEmployee,
    deactivateEmployee,
  };
};

export const useAttendance = (employeeId?: string) => {
  const [todayAttendance, setTodayAttendance] = useState<Attendance | null>(
    null
  );
  const [attendanceHistory, setAttendanceHistory] = useState<Attendance[]>([]);
  const [organizationAttendance, setOrganizationAttendance] = useState<
    Attendance[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const ownerId = getHROwnerId(user);

  const fetchTodayAttendance = useCallback(
    async (empId?: string) => {
      const targetEmployeeId = empId || employeeId;
      if (!targetEmployeeId) return;

      try {
        setLoading(true);
        setError(null);
        const attendance = await hrServices.attendance.getAttendanceByDate(
          targetEmployeeId,
          new Date()
        );
        setTodayAttendance(attendance);
      } catch (err) {
        console.error('Error fetching today attendance:', err);
        setError('فشل في تحميل حضور اليوم.');
      } finally {
        setLoading(false);
      }
    },
    [employeeId]
  );

  const fetchAttendanceHistory = useCallback(
    async (startDate: Date, endDate: Date, empId?: string) => {
      const targetEmployeeId = empId || employeeId;
      if (!targetEmployeeId) return;

      try {
        setLoading(true);
        setError(null);
        const history = await hrServices.attendance.getAttendanceByPeriod(
          targetEmployeeId,
          startDate,
          endDate
        );
        setAttendanceHistory(history);
      } catch (err) {
        console.error('Error fetching attendance history:', err);
        setError('فشل في تحميل سجل الحضور.');
      } finally {
        setLoading(false);
      }
    },
    [employeeId]
  );

  const fetchOrganizationAttendance = useCallback(
    async (employees: Employee[], startDate: Date, endDate: Date) => {
      try {
        setLoading(true);
        setError(null);
        const records = await hrServices.attendance.getAttendanceForEmployees(
          employees.map(employee => employee.id),
          startDate,
          endDate,
          ownerId
        );
        setOrganizationAttendance(records);
        return records;
      } catch (err) {
        console.error('Error fetching organization attendance:', err);
        setError('فشل في تحميل حضور المؤسسة.');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [ownerId]
  );

  const checkIn = useCallback(
    async (location?: {
      latitude: number;
      longitude: number;
      address?: string;
    }) => {
      const targetEmployeeId = employeeId;
      if (!targetEmployeeId) return;

      try {
        setError(null);
        await hrServices.attendance.checkIn(targetEmployeeId, location);
        await fetchTodayAttendance(targetEmployeeId);
      } catch (err) {
        console.error('Error checking in:', err);
        setError('فشل في تسجيل الحضور.');
        throw err;
      }
    },
    [employeeId, fetchTodayAttendance]
  );

  const checkOut = useCallback(async () => {
    const targetEmployeeId = employeeId;
    if (!targetEmployeeId) return;

    try {
      setError(null);
      await hrServices.attendance.checkOut(targetEmployeeId);
      await fetchTodayAttendance(targetEmployeeId);
    } catch (err) {
      console.error('Error checking out:', err);
      setError('فشل في تسجيل الانصراف.');
      throw err;
    }
  }, [employeeId, fetchTodayAttendance]);

  const recordManualAttendance = useCallback(
    async (
      attendanceData: Omit<
        Attendance,
        | 'id'
        | 'createdAt'
        | 'updatedAt'
        | 'breaks'
        | 'regularHours'
        | 'overtimeHours'
        | 'totalHours'
      > &
        Partial<
          Pick<
            Attendance,
            'breaks' | 'regularHours' | 'overtimeHours' | 'totalHours'
          >
        >
    ) => {
      try {
        setError(null);
        return await hrServices.attendance.recordManualAttendance(
          attendanceData
        );
      } catch (err) {
        console.error('Error recording manual attendance:', err);
        setError('فشل في تسجيل الحضور اليدوي.');
        throw err;
      }
    },
    []
  );

  useEffect(() => {
    if (employeeId) void fetchTodayAttendance();
  }, [employeeId, fetchTodayAttendance]);

  return {
    todayAttendance,
    attendanceHistory,
    organizationAttendance,
    loading,
    error,
    fetchTodayAttendance,
    fetchAttendanceHistory,
    fetchOrganizationAttendance,
    checkIn,
    checkOut,
    recordManualAttendance,
  };
};

export const useLeaves = (employeeId?: string) => {
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [organizationLeaves, setOrganizationLeaves] = useState<Leave[]>([]);
  const [pendingLeaves, setPendingLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const ownerId = getHROwnerId(user);

  const fetchEmployeeLeaves = useCallback(
    async (empId?: string) => {
      const targetEmployeeId = empId || employeeId;
      if (!targetEmployeeId) return;

      try {
        setLoading(true);
        setError(null);
        const employeeLeaves = await hrServices.leaves.getEmployeeLeaves(
          targetEmployeeId
        );
        setLeaves(employeeLeaves);
      } catch (err) {
        console.error('Error fetching employee leaves:', err);
        setError('فشل في تحميل بيانات الإجازات.');
      } finally {
        setLoading(false);
      }
    },
    [employeeId]
  );

  const fetchPendingLeaves = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const pending = await hrServices.leaves.getPendingLeaves(ownerId);
      setPendingLeaves(pending);
    } catch (err) {
      console.error('Error fetching pending leaves:', err);
      setError('فشل في تحميل طلبات الإجازة المعلقة.');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  const fetchOrganizationLeaves = useCallback(
    async (ownerIdOverride?: string) => {
      const targetOwnerId = ownerIdOverride || getHROwnerId(user);
      if (!targetOwnerId) return;

      try {
        setLoading(true);
        setError(null);
        const records = await hrServices.leaves.getLeavesByOwner(targetOwnerId);
        setOrganizationLeaves(records);
      } catch (err) {
        console.error('Error fetching organization leaves:', err);
        setError('فشل في تحميل إجازات المؤسسة.');
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const createLeave = useCallback(
    async (leaveData: Omit<Leave, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        setError(null);
        const newLeaveId = await hrServices.leaves.createLeave(leaveData);
        await fetchEmployeeLeaves(leaveData.employeeId);
        await fetchOrganizationLeaves(leaveData.createdBy);
        return newLeaveId;
      } catch (err) {
        console.error('Error creating leave:', err);
        setError('فشل في إنشاء طلب الإجازة.');
        throw err;
      }
    },
    [fetchEmployeeLeaves, fetchOrganizationLeaves]
  );

  const approveLeave = useCallback(
    async (leaveId: string, notes?: string) => {
      if (!user?.id) return;

      try {
        setError(null);
        await hrServices.leaves.approveLeave(leaveId, user.id, notes);
        await fetchOrganizationLeaves();
        await fetchPendingLeaves();
      } catch (err) {
        console.error('Error approving leave:', err);
        setError('فشل في اعتماد الإجازة.');
        throw err;
      }
    },
    [user?.id, fetchPendingLeaves, fetchOrganizationLeaves]
  );

  const rejectLeave = useCallback(
    async (leaveId: string, notes?: string) => {
      if (!user?.id) return;

      try {
        setError(null);
        await hrServices.leaves.rejectLeave(leaveId, user.id, notes);
        await fetchOrganizationLeaves();
        await fetchPendingLeaves();
      } catch (err) {
        console.error('Error rejecting leave:', err);
        setError('فشل في رفض الإجازة.');
        throw err;
      }
    },
    [user?.id, fetchPendingLeaves, fetchOrganizationLeaves]
  );

  useEffect(() => {
    if (employeeId) void fetchEmployeeLeaves();
  }, [employeeId, fetchEmployeeLeaves]);

  return {
    leaves,
    organizationLeaves,
    pendingLeaves,
    loading,
    error,
    fetchEmployeeLeaves,
    fetchOrganizationLeaves,
    fetchPendingLeaves,
    createLeave,
    approveLeave,
    rejectLeave,
  };
};

export const usePayroll = (employeeId?: string) => {
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [organizationPayrolls, setOrganizationPayrolls] = useState<Payroll[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchEmployeePayrolls = useCallback(
    async (empId?: string) => {
      const targetEmployeeId = empId || employeeId;
      if (!targetEmployeeId) return;

      try {
        setLoading(true);
        setError(null);
        const employeePayrolls = await hrServices.payroll.getEmployeePayrolls(
          targetEmployeeId
        );
        setPayrolls(employeePayrolls);
      } catch (err) {
        console.error('Error fetching employee payrolls:', err);
        setError('فشل في تحميل كشوف الرواتب.');
      } finally {
        setLoading(false);
      }
    },
    [employeeId]
  );

  const fetchOrganizationPayrolls = useCallback(
    async (ownerIdOverride?: string) => {
      const targetOwnerId = ownerIdOverride || getHROwnerId(user);
      if (!targetOwnerId) return;

      try {
        setLoading(true);
        setError(null);
        const records =
          await hrServices.payroll.getPayrollsByOwner(targetOwnerId);
        setOrganizationPayrolls(records);
      } catch (err) {
        console.error('Error fetching organization payrolls:', err);
        setError('فشل في تحميل كشوف رواتب المؤسسة.');
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const calculatePayroll = useCallback(
    async (empId: string, month: number, year: number) => {
      try {
        setError(null);
        return await hrServices.payroll.calculatePayroll(empId, month, year);
      } catch (err) {
        console.error('Error calculating payroll:', err);
        setError('فشل في حساب كشف الراتب.');
        throw err;
      }
    },
    []
  );

  const createPayroll = useCallback(
    async (payrollData: Omit<Payroll, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        setError(null);
        const newPayrollId = await hrServices.payroll.createPayroll(
          payrollData
        );
        await fetchEmployeePayrolls(payrollData.employeeId);
        await fetchOrganizationPayrolls(payrollData.createdBy);
        return newPayrollId;
      } catch (err) {
        console.error('Error creating payroll:', err);
        setError('فشل في إنشاء كشف الراتب.');
        throw err;
      }
    },
    [fetchEmployeePayrolls, fetchOrganizationPayrolls]
  );

  const approvePayroll = useCallback(
    async (payrollId: string) => {
      if (!user?.id) return;

      try {
        setError(null);
        await hrServices.payroll.approvePayroll(payrollId, user.id);
        await fetchOrganizationPayrolls();
      } catch (err) {
        console.error('Error approving payroll:', err);
        setError('فشل في اعتماد كشف الراتب.');
        throw err;
      }
    },
    [user?.id, fetchOrganizationPayrolls]
  );

  const markPayrollPaid = useCallback(
    async (payrollId: string, paymentSource: 'cash' | 'bank' = 'cash') => {
      try {
        setError(null);
        await hrServices.payroll.markPayrollPaid(payrollId, paymentSource);
        await fetchOrganizationPayrolls();
      } catch (err) {
        console.error('Error marking payroll paid:', err);
        setError('فشل في تسجيل صرف الراتب.');
        throw err;
      }
    },
    [fetchOrganizationPayrolls]
  );

  useEffect(() => {
    if (employeeId) void fetchEmployeePayrolls();
  }, [employeeId, fetchEmployeePayrolls]);

  return {
    payrolls,
    organizationPayrolls,
    loading,
    error,
    fetchEmployeePayrolls,
    fetchOrganizationPayrolls,
    calculatePayroll,
    createPayroll,
    approvePayroll,
    markPayrollPaid,
  };
};

export const useHR = (employeeId?: string) => {
  const employees = useEmployees();
  const attendance = useAttendance(employeeId);
  const leaves = useLeaves(employeeId);
  const payroll = usePayroll(employeeId);

  return {
    employees,
    attendance,
    leaves,
    payroll,
  };
};

export default useHR;
